import { useEffect, useMemo, useRef, useState } from "react";
import type { GeneralProductionResponse, GeneralProductionShift } from "../../../shared/general-production";
import { generalProductionResponseSchema } from "../../../shared/general-production";
import { apiRequest } from "../../shared/api/client";
import type { GeneralProductionFilters } from "./general-production-model";
import { defaultGeneralProductionView, readGeneralProductionView, reconcileGeneralProductionView, writeGeneralProductionView } from "./general-production-view-state";

const initialView = () => readGeneralProductionView(typeof window === "undefined" ? "" : window.location.search);
export function useGeneralProductionWorkspace() {
  const [initial] = useState(initialView);
  const [view, setView] = useState(initial.view);
  const [notice, setNotice] = useState(initial.notice);
  const [data, setData] = useState<GeneralProductionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const request = useRef<AbortController | null>(null);
  const resolvedPlan = useRef("");
  const { planId, shift } = view;
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  const effectiveFilters = useMemo(() => data ? reconcileGeneralProductionView(view, data, today).view.filters : view.filters, [data, today, view]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const restore = () => {
      if (!/^\/genel-uretim\/?$/u.test(window.location.pathname)) return;
      request.current?.abort(); setData(null); resolvedPlan.current = "";
      const restored = initialView(); setView(restored.view); setNotice(restored.notice);
      setAttempt((value) => value + 1);
    };
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!/^\/genel-uretim\/?$/u.test(window.location.pathname)) return;
    const search = writeGeneralProductionView(window.location.search, { ...view, planId: view.planId || data?.selectedPlan?.id || resolvedPlan.current });
    const url = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
    if (url !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState(window.history.state, "", url);
    }
  }, [view, data?.selectedPlan?.id]);
  useEffect(() => {
    request.current?.abort(); const controller = new AbortController(); request.current = controller;
    const params = new URLSearchParams();
    const requestedPlan = planId || resolvedPlan.current;
    if (requestedPlan) params.set("planId", requestedPlan);
    if (shift !== "ALL") params.set("shift", shift);
    setLoading(true); setError("");
    void apiRequest<unknown>(`/api/production/general-production${params.size ? `?${params}` : ""}`, { cache: "no-store", signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return;
        const parsed = generalProductionResponseSchema.parse(response);
        resolvedPlan.current = parsed.selectedPlan?.id || "";
        setData(parsed);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setData(null); setError(caught instanceof Error ? caught.message : "Genel üretim verisi alınamadı.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [attempt, planId, shift]);
  useEffect(() => {
    if (!data || loading) return;
    const reconciled = reconcileGeneralProductionView(view, data, today);
    if (reconciled.view.filters.date !== view.filters.date || reconciled.view.filters.line !== view.filters.line) {
      setView((current) => current === view ? reconciled.view : current);
    }
    if (reconciled.notice) setNotice(reconciled.notice);
  }, [data, loading, today, view]);
  return {
    data, planId: planId || resolvedPlan.current, shift, filters: effectiveFilters,
    hideCompleted: view.hideCompleted, notice, loading, error,
    selectPlan: (id: string) => {
      if (id !== planId) { request.current?.abort(); setData(null); resolvedPlan.current = ""; }
      setNotice("");
      setView((current) => ({ ...current, planId: id, filters: { ...current.filters, date: "" } }));
    },
    selectShift: (value: GeneralProductionShift | "ALL") => { if (value === shift) return; setNotice(""); request.current?.abort(); setView((current) => ({ ...current, shift: value })); },
    updateFilter: (field: keyof GeneralProductionFilters, value: string) => {
      setNotice(""); setView((current) => ({ ...current, filters: { ...current.filters, [field]: value } }));
    },
    toggleCompleted: () => setView((current) => ({ ...current, hideCompleted: !current.hideCompleted })),
    resetFilters: () => {
      setNotice(""); setView((current) => ({ ...current, filters: { ...defaultGeneralProductionView().filters, date: "ALL" }, hideCompleted: false }));
    },
    openDefaultPlan: () => {
      request.current?.abort(); setData(null); resolvedPlan.current = "";
      setNotice("Bağlantıdaki plan yerine varsayılan plan açılıyor; kayıtlar değiştirilmedi.");
      setView((current) => ({ ...current, planId: "", filters: { ...current.filters, date: "", line: "ALL" } }));
      setAttempt((value) => value + 1);
    },
    retry: () => setAttempt((value) => value + 1),
  };
}
